import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, BancAccauntVerification, Box, useResponsive } from '@common-fe/common-fe';

import { usePlaid } from '@/utils/plaid/hooks/usePlaid';
import useSubmitPlaidBankAccountActivationQuery from '@/utils/plaid/queries/useSubmitPlaidBankAccountActivation.query';

import Banner from '../Banner/Banner';

import { usePlaidBannerModalStore } from './store';
import { AccountWithVerificationStatus } from './useGetAccountsWithVerificationStatus.query';

interface Props {
  data?: AccountWithVerificationStatus;
  refetch?: () => void;
}

const BannerPlaidVerification: React.FC<Props> = ({ data, refetch }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const elevateAccountId = useMemo(() => data?.id as string, [data]);
  const { updateBankStatus } = useSubmitPlaidBankAccountActivationQuery();
  const setErrorModal = usePlaidBannerModalStore((state) => state.setErrorModal);
  const setIsPlaidLoading = usePlaidBannerModalStore((state) => state.setIsPlaidLoading);
  const setSuccessModal = usePlaidBannerModalStore((state) => state.setSuccessModal);
  const handleUpdateBankStatus = useCallback(async () => {
    try {
      setIsPlaidLoading(true);
      await updateBankStatus({ elevate_account_id: elevateAccountId });
      setSuccessModal(true);
      setIsPlaidLoading(false);
    } catch (e) {
      setErrorModal(true);
      setIsPlaidLoading(false);
    }
  }, [elevateAccountId, setErrorModal, setIsPlaidLoading, setSuccessModal, updateBankStatus]);
  const { openPlaid, isLoading } = usePlaid({
    accountId: elevateAccountId,
    onSuccess: handleUpdateBankStatus,
    onExit: refetch,
    isManualVerification: true,
  });

  return (
    <Banner
      title={t('Complete Bank Account Verification')}
      description={t(
        'A small deposit will be posted to your account within one to two business days. Once posted, you will need to provide additional details to verify your account.'
      )}
      icon={<BancAccauntVerification />}
      testId="BannerPlaidVerification"
    >
      <Box margin={{ top: 'spacing24' }}>
        <AppButton
          onClick={openPlaid}
          fill={isMobile}
          width="180px"
          testId="BannerPlaidVerification_Verify"
          disabled={isLoading}
        >
          Verify Account
        </AppButton>
      </Box>
    </Banner>
  );
};

export default BannerPlaidVerification;
